import { useCallback } from "react";
import { LayerTypeControlPageProps } from "../../types";
import { LPA_DISPLAY_BY, usePlanningAuthorityLayerTypeContext } from "./PlanningAuthorityContext";
import { NumericInput } from "react-migration/components/NumericInput";
import { Select } from "react-migration/components/Select";
import clsx from "clsx";
import { ConstraintLayerConfig } from "../ConstraintsLayerType";
import { useLayerStore } from "../ConstraintsLayerType/ConstraintsContext/store";
import { DesignationHatch } from "react-migration/domains/constraints/components/Hatch";

import { ModalV2 } from "react-migration/components/ModalV2/ModalV2";
import { GenericCategoryFilter } from "react-migration/layouts/map/Constraints/GenericCategoryFilter";
import { ConstraintsCategory } from "react-migration/layouts/map/Constraints/types";
import { useControlTracking } from "../../ControlTrackingContext";
import { RenderNodeBadge } from "react-migration/components/CheckboxTree/types";

export function PlanningAuthorityControlPage({ layer, disabled }: LayerTypeControlPageProps) {
  const { debouncedTrackControlChange } = useControlTracking();

  const {
    displayByFilter,
    localPlanAgeFilter,
    housingLandSupplyRemainingFilter,
    housingDeliveryTestFilter,
    setDisplayByFilter,
    setLocalPlanAgeFilter,
    setHousingLandSupplyRemainingFilter,
    setHousingDeliveryTestFilter,
    constraintsStore,
    dispatch,
  } = usePlanningAuthorityLayerTypeContext();

  const handleDisplayByChange = useCallback(
    (value: LPA_DISPLAY_BY) => {
      debouncedTrackControlChange({
        filterName: "displayBy",
        filterState: value,
      });
      setDisplayByFilter(value);
    },
    [setDisplayByFilter, debouncedTrackControlChange]
  );

  const handleLocalPlanAgeChange = useCallback(
    (localPlanAge: number) => {
      debouncedTrackControlChange({ filterName: "localPlanAge", filterState: localPlanAge });
      setLocalPlanAgeFilter(localPlanAge);
    },
    [setLocalPlanAgeFilter, debouncedTrackControlChange]
  );

  const handleHousingLandSupplyChange = useCallback(
    (housingLandSupply: number) => {
      debouncedTrackControlChange({
        filterName: "housingLandSupply",
        filterState: housingLandSupply,
      });
      setHousingLandSupplyRemainingFilter(housingLandSupply);
    },
    [setHousingLandSupplyRemainingFilter, debouncedTrackControlChange]
  );

  const handleHousingDeliveryTestChange = useCallback(
    (housingDeliveryTest: number) => {
      debouncedTrackControlChange({
        filterName: "housingDeliveryTest",
        filterState: housingDeliveryTest,
      });
      setHousingDeliveryTestFilter(housingDeliveryTest);
    },
    [setHousingDeliveryTestFilter, debouncedTrackControlChange]
  );

  const [layerState, layerActions, layerSelectors] = useLayerStore(layer.id, {
    constraintsStore,
    dispatch,
  });

  const renderNodeBadge = useCallback<RenderNodeBadge>(({ id }) => <DesignationHatch id={id} />, []);

  const keyModal = (
    <ModalV2.Root>
      <ModalV2.Trigger>
        <i className="text-lg icon-lt-question-line text-text-base-tertiary" />
      </ModalV2.Trigger>
      <ModalV2.Body title="Local Planning Authorities">
        <div className="flex flex-col between">
          <p>Insights into local planning authorities can help you target your development to an opportune area</p>
          <div className="pt-1">
            <p className="font-semibold">Boundaries</p>
            <p>Visualise a planning authority using its boundary</p>
          </div>
          <div className="pt-1 pb-2.5">
            <p className="font-semibold">Presumption in favour</p>
            <div className="flex items-center pl-3">
              <div className="w-9 h-4 bg-[#0D9488]" />
              <div className="ml-2">Presumption in favour is likely to apply</div>
            </div>
            <div className="flex items-center pl-3">
              <div className="w-9 h-4 bg-[#F97316]" />
              <div className="ml-2">Presumption in favour is unlikely to apply</div>
            </div>
            <div className="flex items-center pl-3">
              <div className="w-9 h-4 bg-[#808080]" />
              <div className="ml-2">Presumption in favour status is unknown</div>
            </div>
          </div>
          <div className="pt-1">
            <p className="font-semibold">Call for sites</p>
            <div className="flex items-center pl-3">
              <div className="w-9 h-4 bg-[#0D9488]" />
              <div className="ml-2">An active call for sites is published</div>
            </div>
          </div>
        </div>
      </ModalV2.Body>
    </ModalV2.Root>
  );

  const categorySchema = (layer.layerConfig as ConstraintLayerConfig).categorySchema;

  return (
    <>
      <div className="flex flex-col gap-1">
        <div
          className={clsx("flex flex-row items-center gap-1 text-sm font-medium", {
            "opacity-50": disabled,
          })}
        >
          <span>Display by</span>
          {keyModal}
        </div>
        <Select value={displayByFilter} disabled={disabled} onValueChange={handleDisplayByChange}>
          <Select.Option value={LPA_DISPLAY_BY.Boundaries}>Boundaries</Select.Option>
          <Select.Option value={LPA_DISPLAY_BY.PIF}>Presumption in favour</Select.Option>
          <Select.Option value={LPA_DISPLAY_BY.CallForSites}>Call for sites</Select.Option>
        </Select>
      </div>
      <GenericCategoryFilter
        id={ConstraintsCategory.LOCAL_PLANNING_AUTHORITY}
        disabled={disabled}
        categorySchema={categorySchema}
        visibleCategoryIds={layerState.visibleCategories}
        categoryTree={layerSelectors.getCategoryTree()}
        setVisibleCategories={layerActions.setVisibleCategories}
        renderNodeBadge={renderNodeBadge}
      />
      <div
        className={clsx("text-base py-3", {
          "opacity-50": disabled,
        })}
      >
        Filters
      </div>
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <label
            htmlFor="local-plan-age"
            className={clsx("font-semibold", {
              "opacity-50": disabled,
            })}
          >
            Local plans older than
          </label>
          <div className="flex items-center gap-2">
            <NumericInput
              id="local-plan-age"
              className="max-w-[80px]"
              size="small"
              value={localPlanAgeFilter}
              minValue={0}
              maxValue={10}
              onChange={handleLocalPlanAgeChange}
              isDisabled={disabled}
              placeholder="any"
            />
            <div className="opacity-50">years</div>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label
            htmlFor="housing-land-supply"
            className={clsx("font-semibold", {
              "opacity-50": disabled,
            })}
          >
            Housing land supply remaining
          </label>
          <div className="flex items-center gap-2">
            <NumericInput
              id="housing-land-supply"
              className="max-w-[80px]"
              size="small"
              value={housingLandSupplyRemainingFilter}
              minValue={0}
              maxValue={10}
              onChange={handleHousingLandSupplyChange}
              isDisabled={disabled}
              placeholder="any"
            />
            <div className="opacity-50">years or less</div>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label
            htmlFor="housing-delivery-test"
            className={clsx("font-semibold", {
              "opacity-50": disabled,
            })}
          >
            Housing delivery test
          </label>
          <div className="flex items-center gap-2">
            <NumericInput
              id="housing-delivery-test"
              className="max-w-[80px]"
              size="small"
              value={housingDeliveryTestFilter}
              minValue={0}
              maxValue={1000}
              onChange={handleHousingDeliveryTestChange}
              isDisabled={disabled}
              placeholder="any"
            />
            <div className="pr-6 opacity-50">% or less</div>
          </div>
        </div>
      </div>
    </>
  );
}
